/**
 * Centralized utility for image metadata processing, focusing on Alt Text Remediation.
 */

/**
 * Determines if text is "good" alt text, or garbage/useless text.
 */
function isUsefulAltText(alt: string | undefined | null): boolean {
    if (!alt) return false;
    const clean = alt.trim().toLowerCase();
    if (!clean) return false;

    // Reject exact useless phrases
    const uselessPhrases = ['image', 'photo', 'img', 'picture', 'thumbnail', 'attached'];
    if (uselessPhrases.includes(clean)) return false;

    // Reject purely numeric strings
    if (/^\d+(\.\d+)?$/.test(clean)) return false;

    // Reject raw UUIDs
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(clean)) return false;

    // Reject strings that look heavily like unparsed filenames (with extensions)
    if (/\.(jpg|jpeg|png|webp|gif|avif)$/.test(clean)) return false;

    // Reject numeric timestamp-heavy strings
    if (/^\d{10,14}[-_]?/.test(clean)) return false;

    return true;
}

/**
 * Derives readable, human-friendly alt text directly from an image filename layout.
 */
export function deriveAltTextFromFilename(filenameOrUrl: string | undefined | null): string {
    if (!filenameOrUrl) return '';

    // Extract the final segment (the filename) from the path/url
    let filename = filenameOrUrl.split('/').pop() || filenameOrUrl;
    filename = filename.split(/[?#]/)[0]; // Remove query params

    // Strip broken double extensions (e.g., .jpg.jpg, .webp.webp)
    filename = filename.replace(/\.(jpg|jpeg|png|webp|gif|avif)+$/i, '');
    
    // Strip normal extension
    filename = filename.replace(/\.(jpg|jpeg|png|webp|gif|avif)$/i, '');

    // 1. Remove Prefix: Timestamps (13 digits), followed by optional dashes and another number (ID)
    // E.g., "1774882182769-613769520-"
    filename = filename.replace(/^\d{13}-\d+-/, '');

    // 2. Remove UUIDs
    filename = filename.replace(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/g, '');

    // 3. Remove arbitrary size slugs like "-1024x768" or "-scaled"
    filename = filename.replace(/-\d{2,5}x\d{2,5}/g, '');
    filename = filename.replace(/-scaled/g, '');

    // 4. Remove isolated alphanumeric hashes, e.g., "-a3f912cd"
    filename = filename.replace(/-[0-9a-fA-F]{8,32}\b/g, '');

    // Convert hyphens and underscores to spaces
    let readable = filename.replace(/[-_]/g, ' ');

    // Remove domains or 'coms' if stuck together (Like TopGearcoms -> Top Gear coms, but let's just strip 'coms' if awkward)
    // Add spaces between camelCase if present, but be careful not to break acronyms
    readable = readable.replace(/([a-z])([A-Z])/g, '$1 $2');
    
    readable = readable.replace(/coms\b/gi, 'com'); // TopGearcoms -> TopGearcom -> Top Gear com

    // Clean up multiple spaces and trim
    readable = readable.replace(/\s+/g, ' ').trim();

    // Capitalize the first letter nicely
    if (readable.length > 0) {
        readable = readable.charAt(0).toUpperCase() + readable.slice(1);
    }

    return readable;
}

/**
 * The master function to resolve the perfect alt text.
 * Determines if the image is decorative. If the original alt is good, it keeps it.
 * Otherwise it rebuilds it from the source url.
 */
export function resolveAltText(
    originalAlt: string | null | undefined, 
    src: string | null | undefined,
    isDecorative: boolean = false
): string {
    if (isDecorative) return ''; // Screen readers cleanly skip empty strings in alt

    if (isUsefulAltText(originalAlt)) {
        return (originalAlt || '').trim();
    }

    // Attempt to derive from the file name
    const derived = deriveAltTextFromFilename(src);
    if (derived.length > 2) {
        return derived;
    }

    // Fallback if deriving fails
    return 'Image'; 
}
